import { z } from "zod";

import type { IssuedRevisionsApi } from "./issues";

/**
 * Project files (docs/prd/03-platform-architecture.md §11; FR-PROJ-117; RFC 0002 §8.1, §11 decision 3): the files a
 * project holds, every version kept. The Files page lists, uploads and downloads them, and Draft's file commands
 * read and write them instead of a local disk. The doc service serves them (`crates/services/doc/src/files`); in
 * local mode this browser keeps them.
 */

export const versionSchema = z.object({
  version: z.number().int().positive(),
  sizeBytes: z.number().int().nonnegative(),
  sha256: z.string(),
  contentType: z.string(),
  uploadedBy: z.string(),
  /** ISO 8601, UTC. */
  uploadedAt: z.string(),
  /** The save-back session that wrote it (D-062): the Files page lists a session's versions as one entry. */
  session: z.string().optional(),
  /** The drawing it was written from by name (an xref edited in place there). */
  writtenFrom: z.string().optional(),
});

export const fileSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  /** The folder it is in, or null at the top level (a service from before folders sends none). */
  folder: z.string().nullable().optional(),
  /** The latest version's number and details. */
  version: z.number().int().positive(),
  sizeBytes: z.number().int().nonnegative(),
  contentType: z.string(),
  uploadedBy: z.string(),
  uploadedAt: z.string(),
  /** Every version, the latest first. */
  versions: z.array(versionSchema).min(1),
});

export const listSchema = z.object({ files: z.array(fileSchema) });

/** A folder in a project's files (FR-PROJ-117): its place among those beside it, smallest first. */
export const folderSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  /** The folder it is in, or null at the top level. */
  parent: z.string().nullable(),
  position: z.number().int(),
  createdBy: z.string(),
  createdAt: z.string(),
});

export const folderListSchema = z.object({ folders: z.array(folderSchema) });

export type ProjectFolder = z.infer<typeof folderSchema>;

/** What a folder change asks for; left out, that part stays. `parent: null` moves it to the top. */
export interface FolderChange {
  readonly name?: string;
  readonly parent?: string | null;
  readonly position?: number;
}

/** A deleted file, in its project's Trash until `purgeAfter`, when it is removed for good. */
export const trashedFileSchema = fileSchema.extend({
  /** ISO 8601, UTC. */
  deletedAt: z.string(),
  deletedBy: z.string(),
  /** ISO 8601, UTC: when it is removed for good, every version. */
  purgeAfter: z.string(),
});

export const trashListSchema = z.object({ files: z.array(trashedFileSchema) });

/** The drawings whose xrefs follow a file (D-062): their IDs on the doc service. */
export const dependentsSchema = z.object({ drawings: z.array(z.string()) });

export type ProjectFileVersion = z.infer<typeof versionSchema>;
export type ProjectFile = z.infer<typeof fileSchema>;
export type TrashedFile = z.infer<typeof trashedFileSchema>;

/** What an upload carries. */
export interface Upload {
  readonly bytes: Uint8Array;
  readonly contentType: string;
  /**
   * Who this browser believes uploads it, for files kept in this browser. The doc service always takes the signed-in
   * person instead.
   */
  readonly uploadedBy: string;
  /** A drawing saving back to its file (D-062), not an upload: the same bytes as the latest version record nothing. */
  readonly save?: SaveBackMark | undefined;
}

/** What marks a save back: its editing session, and the host drawing it was written from (an xref edited in place). */
export interface SaveBackMark {
  readonly session: string;
  readonly from?: string | undefined;
}

/** Which file a big upload is for: the project's file of this name (a new one if there is none), or a new version of this file. */
export type UploadTarget = { readonly name: string } | { readonly fileId: string };

/** What an upload of a picked file carries besides its bytes. */
export interface FileUploadOptions {
  readonly contentType: string;
  /** As `Upload.uploadedBy`. */
  readonly uploadedBy: string;
  /** Called as bytes go, with how many have gone and how many there are. */
  readonly onProgress?: ((sent: number, total: number) => void) | undefined;
  /** Aborting it stops the upload and throws away what went up. */
  readonly signal?: AbortSignal | undefined;
}

/** The DWG versions Orthos saves (D-016): 2018 by default, and 2013 and 2010 for older AutoCAD. */
export const DWG_VERSIONS = ["2018", "2013", "2010"] as const;
export type DwgVersion = (typeof DWG_VERSIONS)[number];

/** A conversion's bytes, and what it left out or changed, one sentence each. */
export interface Converted {
  readonly bytes: Uint8Array;
  readonly notes: readonly string[];
}

/** A drawing saved as a DWG project file, and what the conversion left out. */
export interface SavedDwg {
  readonly file: ProjectFile;
  readonly notes: readonly string[];
}

/** The project files backend as pages and dialogs see it. */
export interface ProjectFilesApi extends IssuedRevisionsApi {
  /** The project's files, the most recently changed first. */
  list(projectId: string): Promise<ProjectFile[]>;
  /** A new file, or the next version of the project's file of that name (names compare without case). */
  upload(projectId: string, name: string, upload: Upload): Promise<ProjectFile>;
  /** The next version of a file. */
  addVersion(projectId: string, fileId: string, upload: Upload): Promise<ProjectFile>;
  /**
   * A file picked on this computer as a new file or a new version, however big: in the doc service the file goes
   * straight to its storage in parts, with progress, past the host's cap on request bodies.
   */
  uploadFile(
    projectId: string,
    target: UploadTarget,
    file: Blob,
    options: FileUploadOptions,
  ): Promise<ProjectFile>;
  /** A version's bytes, the latest unless `version` says which. */
  download(projectId: string, fileId: string, version?: number): Promise<Uint8Array>;
  /**
   * Where the browser can download a version from, for a big one: a short-lived link to the storage, so the bytes
   * never pass through the doc service. `undefined` for one that downloads with `download`.
   */
  downloadLink(projectId: string, fileId: string, version?: number): Promise<string | undefined>;
  /**
   * Where a version's raster tile pyramid is on the doc service (FR-IO-102, 095): an image's page, or a PDF's page.
   * `undefined` for files kept in this browser, which the browser decodes itself.
   */
  rasterEndpoint(
    projectId: string,
    fileId: string,
    version: number,
    page: number,
  ): RasterEndpoint | undefined;
  /**
   * A DWG version's DXF form, which the engine reads (WS-06): the doc service converts it once per version and keeps
   * it, so the browser and the service read the same bytes. The latest version unless `version` says which.
   */
  dxfOf(projectId: string, fileId: string, version?: number): Promise<Converted>;
  /** A drawing's DXF saved as a DWG of `version`: a new file, or the next version of the file of that name. */
  saveDwg(projectId: string, name: string, dxf: Uint8Array, version: DwgVersion): Promise<SavedDwg>;
  /** A drawing's DXF as DWG bytes of `version`, to download. */
  toDwg(projectId: string, dxf: Uint8Array, version: DwgVersion): Promise<Converted>;
  /** The project's folders, each place's in order. */
  folders(projectId: string): Promise<ProjectFolder[]>;
  /** A new folder at the top or in `parent`, after those beside it (Editors and up). */
  createFolder(projectId: string, name: string, parent: string | null): Promise<ProjectFolder>;
  /** Renames, moves or reorders a folder. */
  updateFolder(projectId: string, folderId: string, change: FolderChange): Promise<ProjectFolder>;
  /** Deletes an empty folder. */
  deleteFolder(projectId: string, folderId: string): Promise<void>;
  /** Moves a file into a folder, or to the top (`null`). */
  moveFile(projectId: string, fileId: string, folder: string | null): Promise<ProjectFile>;

  /**
   * Moves a file, every version, to the project's Trash, where it can be restored until it is removed for good.
   * `deletedBy` is who this browser believes deletes it, for files kept here; the doc service takes the signed-in person.
   */
  remove(projectId: string, fileId: string, deletedBy: string): Promise<TrashedFile>;
  /** The project's Trash, the most recently deleted first. */
  trash(projectId: string): Promise<TrashedFile[]>;
  /** Takes a file back out of the Trash; refused (`conflict`) while another file has its name. */
  restore(projectId: string, fileId: string): Promise<ProjectFile>;
  /** Removes a file in the Trash for good, every version, now. */
  purge(projectId: string, fileId: string): Promise<void>;
  /**
   * The drawings whose xrefs follow the file, by their doc service IDs (D-062), which the delete warning names.
   * Absent where nothing indexes them (files kept in this browser).
   */
  dependents?(projectId: string, fileId: string): Promise<readonly string[]>;
}

/** Why a files call failed: the service's code and words. */
export type FilesErrorCode =
  | "unauthenticated"
  | "forbidden"
  | "not_found"
  | "gone"
  | "bad_request"
  | "too_large"
  | "unreadable"
  | "conflict"
  | "upload_incomplete"
  | "timed_out"
  | "unavailable"
  | "network"
  | "unexpected";

export class FilesError extends Error {
  readonly code: FilesErrorCode;

  constructor(code: FilesErrorCode, message: string) {
    super(message);
    this.name = "FilesError";
    this.code = code;
  }
}

const BYTES_PER_MB = 1_048_576;

/** A size in megabytes for messages: "48.2 MB". */
export function megabytes(bytes: number): string {
  return `${(bytes / BYTES_PER_MB).toFixed(1)} MB`;
}

/** The refusal for something larger than project files take, before it is sent: what it is, its size and the limit. */
export function tooLarge(what: string, size: number, limit: number): FilesError {
  return new FilesError(
    "too_large",
    `${what} is ${megabytes(size)}; project files take at most ${megabytes(limit)} at a time.`,
  );
}

/** The words for a failure, whatever threw it. */
export function filesErrorText(error: unknown): string {
  return error instanceof Error ? error.message : "Project files are not available right now.";
}

/** A file version's tile pyramid on the doc service (`files/raster/routes.rs`). */
export interface RasterEndpoint {
  /** The pyramid's manifest: size, tile size and levels. */
  readonly manifestUrl: string;
  /** A PDF page's size and whether it is a scan. */
  readonly pageUrl: string;
  /** One WebP tile; level 0 is full size. */
  tileUrl(level: number, col: number, row: number): string;
  /** The page as the tiles a model reads, with their millimetres on the sheet (plan intake). */
  readonly sheetTilesUrl?: string | undefined;
}
